import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ISaveTerminal } from '../models/create-terminal.model';
import { ITerminal } from '../models/terminal.model';
import { IUpdateTerminal } from '../models/edit-terminal.model';
import { ITerminalLookup } from '../models/terminal-lookup.model';
import { IPageResult } from '../../../shared/models/paged-result';
import { ITerminalListRequest } from '../requests/terminal-list.request';
import { HttpService } from '../../../shared/services/http.service';
import { ITerminalCustody } from '../models/terminal-custody.model';
import { ITerminalCustodyRequest } from '../requests/terminal-custody.request';
import { ITerminalTransferListRequest } from '../requests/terminal-transfer-list.request';
import { ITerminalTransferList } from '../models/terminal-transfer-list.model';
import { ITerminalVersionBuildsRequest } from '../requests/terminal-version-builds.request';
import { ITerminalVersionBuilds } from '../models/terminal-version-builds.model';

@Injectable({
  providedIn: 'root',
})
export class TerminalsService {
  private readonly terminalsUrl = '/terminals';
  private terminal!: ITerminal;
  private pageSize = 10;
  private terminalRequest: any = null;
  private pageNumber = 0;

  /**
   * @constructor
   * @param {HttpClient} http : Http client for API calls
   */
  constructor(private http: HttpService<any>) { }

  /**
   * Creates a new Terminal
   * @param {ISaveTerminal} terminal : Terminal object to create
   * @returns {ISaveTerminal} Termninal created
   * @public
   */
  createTerminal(terminal: ISaveTerminal): Observable<ISaveTerminal> {
    return this.http.post(terminal, this.terminalsUrl);
  }

  /**
  //  * Creates a new Terminal batch
   * @param {} terminals : Terminals object to create
   * @returns {} Termninals created
   * @public
   */
  createTerminals(terminals: any) {
    return this.http.post(terminals, this.terminalsUrl + "/batch");
  }

  /**
   * Get a specific terminal
   * @param {number} id : terminal ID
   * @returns {ITerminal} Terminal
   * @public
   */
  getById(id: Number): Observable<ITerminal> {
    return this.http.getById(id, this.terminalsUrl);
  }

  /**
   * Get Terminals
   * @returns {ITerminalLookup} Terminal object to lookup
   * @public
   */
  getTerminalsLookup(): Observable<ITerminalLookup[]> {
    const url = this.terminalsUrl + '/lookup';
    return this.http.getLookup(url);
  }

  /**
   *  Calls base service to get the actual page size
   * @returns actual page size
   */
  getPageSize() {
    return this.pageSize;
  }

  /**
   * Calls base service to set a new page size
   * @param {number} pageSize
   */
  setPageSize(pageSize: number) {
    this.pageSize = pageSize;
  }

  setPageNumber(page: number) {
    this.pageNumber = page;
  }

  getPageNumber() {
    return this.getPageNumber;
  }

  storeTerminalSearch(request: any) {
    this.terminalRequest = request;
  }

  getTerminalSearch() {
    return this.terminalRequest;
  }

  clearTerminalSearch() {
    this.terminalRequest = null;
  }
  /**
   * Get all terminals
   * @param {ITerminal} IPageRequest: pagination object request
   * @returns {ITerminal} Terminals list
   * @public
   */
  getAll(
    request: ITerminalListRequest<ITerminal>
  ): Observable<IPageResult<ITerminal>> {
    const pageSize = this.getPageSize();

    this.storeTerminalSearch(request);

    var params = new HttpParams()
      .set('page', (request.page + 1).toString())
      .set('pageSize', pageSize)
      .set('sort', request.sort.property.toString())
      .set('sortDirection', request.sort.order.toString())
      .set('providerTerminalId', request.providerTerminalId)
      .set('softwareVersion', request.softwareVersion);

    if (request.active !== null) {
      params = params.set('active', request.active);
    }

    if (request.dongleType !== null) {
      params = params.set('dongleType', request.dongleType);
    }

    if (request.platformType !== null && request.platformType !== undefined) {
      params = params.set('platformType', request.platformType);
    }

    if (request.blocked !== null) {
      params = params.set('blocked', request.blocked);
    }

    if (request.halted !== null) {
      params = params.set('halted', request.halted);
    }

    if (request.distributorId > 0) {
      params = params.set('distributorId', request.distributorId);
    }

    if (request.operatorId > 0) {
      params = params.set('operatorId', request.operatorId);
    }

    if (request.marketId > 0) {
      params = params.set('marketId', request.marketId);
    }

    if (request.productId > 0) {
      params = params.set('productId', request.productId);
    }

    if (request.locationId > 0) {
      params = params.set('locationId', request.locationId);
    }

    return this.http.getAll(request, this.terminalsUrl, params);
  }

  /**
   * Get all terminals
   * @param {ITerminal} IPageRequest: pagination object request
   * @returns {ITerminal} Terminals list
   * @public
   */
  getTerminalTransfersList(
    request: ITerminalTransferListRequest<ITerminalTransferList>
  ): Observable<IPageResult<ITerminalTransferList>> {
    const pageSize = this.getPageSize();
    var params = new HttpParams()
      .set('page', (request.page + 1).toString())
      .set('pageSize', pageSize)
      .set('sort', request.sort.property.toString())
      .set('sortDirection', request.sort.order.toString())
      .set('providerTerminalId', request.providerTerminalId)
      .set('distributorId', request.distributorId)
      .set('operatorId', request.operatorId)
      .set('locationId', request.locationId)
      .set('softwareVersion', request.softwareVersion);
    return this.http.getAll(
      request,
      this.terminalsUrl + '/transferList',
      params
    );
  }

  /**
   * Get a specific terminal custodies
   * @param {ITerminalCustody} IPageRequest: pagination object request
   * @returns {ITerminalCustody} Terminal custodies list
   * @public
   */
  getTerminalCustodies(
    request: ITerminalCustodyRequest<ITerminalCustody>
  ): Observable<IPageResult<ITerminalCustody>> {
    const pageSize = this.getPageSize();
    var params = new HttpParams()
      .set('id', request.id)
      .set('page', (request.page + 1).toString())
      .set('pageSize', pageSize)
      .set('sort', request.sort.property.toString())
      .set('sortDirection', request.sort.order.toString());
    return this.http.getAll(request, this.terminalsUrl + '/custodies', params);
  }

  /**
  * Get a specific terminal custodies
  * @param {ITerminalCustody} IPageRequest: pagination object request
  * @returns {ITerminalCustody} Terminal custodies list
  * @public
  */
  getTerminalVersionBuilds(
    request: ITerminalVersionBuildsRequest<ITerminalCustody>
  ): Observable<IPageResult<ITerminalVersionBuilds>> {
    const pageSize = this.getPageSize();
    var params = new HttpParams()
      .set('id', request.id)
      .set('page', (request.page + 1).toString())
      .set('pageSize', pageSize)
      .set('sort', request.sort.property.toString())
      .set('sortDirection', request.sort.order.toString());
    return this.http.getAll(request, this.terminalsUrl + '/version-builds', params);
  }

  /**
   * Updates a terminal. Can be only edit properties, or transfer.
   * @param {IUpdateTerminal} terminal : terminal object to update
   * @returns {IUpdateTerminal} termnial updated
   * @public
   */
  updateTerminal(terminal: IUpdateTerminal) {
    return this.http.patch(terminal, this.terminalsUrl);
  }

  /**
   * Transfers terminals
   * @param {any} terminals : terminal object to transfer
   * @returns {any} 204 status
   * @public
   */
  transferTerminals(terminals: any) {
    const url = this.terminalsUrl + '/transfer';
    return this.http.patch(terminals, url);
  }

  /**
   * Stores the terminal the user is working with
   * @param {ITerminal} terminal: terminal object to store
   * @public
   */
  storeTerminal(terminal: any): void {
    this.terminal = terminal;
  }

  /**
   * Returns the current terminal to use it on transfer/install software
   * @returns
   */
  getTerminal() {
    return this.terminal;
  }

  createTerminalFillBlock(request: any): Observable<any> {
    return this.http.post(request, this.terminalsUrl + '/fill-block');
  }

  removeTerminalFillBlock(request: any) {
    return this.http.patch(request, this.terminalsUrl + '/fill-block' + '/remove');
  }
}
